/**
 * Iter-92/94 — Client administrative documents gallery.
 *
 * Iter-94 changes
 * ---------------
 *   - Single upload mode: local file. Backend pushes it to the client's Drive
 *     folder (if configured by the CEO) — transparent for the user.
 *   - New "Document type" selector: "Contrat cadre" | "Autre" (free text label).
 *   - No more Drive Picker / Drive link tabs on this panel.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Upload, Trash2, ExternalLink, Download, Loader2, Plus } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth";

function fmtSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR") + " " + new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function ClientDocumentsPanel({ clientId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [docType, setDocType] = useState("CONTRAT_CADRE"); // "CONTRAT_CADRE" | "AUTRE"
  const [label, setLabel] = useState("Contrat cadre");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const canDelete = ["CEO", "ADMIN", "GESTIONNAIRE"].includes(user?.role);

  const load = () => {
    setLoading(true);
    api.get(`/clients/${clientId}/documents`)
      .then((r) => setDocs(r.data?.documents || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (clientId) load(); }, [clientId]);

  // When switching to "Contrat cadre", reset the label to the canonical name.
  useEffect(() => {
    if (docType === "CONTRAT_CADRE") setLabel("Contrat cadre");
    else if (label === "Contrat cadre") setLabel("");
  }, [docType]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDialog = () => {
    setDocType("CONTRAT_CADRE");
    setLabel("Contrat cadre");
    setFile(null);
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!label.trim()) { toast.error("Libellé requis"); return; }
    if (!file) { toast.error("Aucun fichier sélectionné"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label);
      fd.append("doc_type", docType);
      await api.post(`/clients/${clientId}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Document uploadé (${file.name})`);
      setDialogOpen(false);
      setFile(null);
      setLabel("Contrat cadre");
      setDocType("CONTRAT_CADRE");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur à l'envoi");
    } finally { setBusy(false); }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Supprimer « ${doc.filename} » ?`)) return;
    try {
      await api.delete(`/clients/${clientId}/documents/${doc.doc_id}`);
      toast.success("Document supprimé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const openDoc = async (doc) => {
    if (doc.storage_kind === "drive") {
      window.open(doc.drive_view_url, "_blank", "noopener");
      return;
    }
    try {
      const r = await api.get(`/clients/${clientId}/documents/${doc.doc_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: doc.mime_type }));
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  return (
    <section className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="client-documents-panel">
      <header className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-zinc-600" />
          <h3 className="font-medium text-sm text-brand-navy">Administrative documents</h3>
          <span className="text-xs font-mono text-zinc-500">({docs.length})</span>
        </div>
        <button
          type="button"
          data-testid="client-doc-upload-btn"
          onClick={openDialog}
          className="inline-flex items-center gap-1.5 px-3 h-8 text-xs rounded-md bg-brand-navy text-white hover:bg-brand-navy/90 font-medium"
        >
          <Plus size={13} /> Upload administrative documents
        </button>
      </header>

      {loading && <div className="p-6 text-sm text-zinc-500 text-center">Chargement…</div>}
      {!loading && docs.length === 0 && (
        <div className="p-8 text-center text-sm text-zinc-500" data-testid="client-docs-empty">
          Aucun document pour ce client.<br />
          <span className="text-xs">Cliquez sur <strong>Upload administrative documents</strong> pour commencer.</span>
        </div>
      )}
      {!loading && docs.length > 0 && (
        <ul className="divide-y divide-zinc-100">
          {docs.map((d) => (
            <li key={d.doc_id} data-testid={`client-doc-${d.doc_id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-zinc-50">
              <div className={`w-8 h-8 rounded-md flex items-center justify-center text-white ${d.storage_kind === "drive" ? "bg-emerald-600" : "bg-brand-navy"}`}>
                {d.storage_kind === "drive" ? <ExternalLink size={14} /> : <FileText size={14} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-brand-navy font-medium truncate flex items-center gap-2">
                  {d.filename}
                  {d.doc_type === "CONTRAT_CADRE" && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono bg-emerald-100 text-emerald-800">Contrat cadre</span>
                  )}
                </div>
                <div className="text-[11px] font-mono text-zinc-500 truncate">
                  {d.label} · v{d.version} · {d.storage_kind === "drive" ? "Google Drive" : fmtSize(d.size_bytes)} · {fmtDate(d.created_at)} · {d.uploaded_by_name || "—"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => openDoc(d)}
                data-testid={`client-doc-open-${d.doc_id}`}
                className="p-2 text-zinc-500 hover:text-brand-navy hover:bg-brand-cream rounded"
                title={d.storage_kind === "drive" ? "Ouvrir sur Drive" : "Télécharger"}
              >
                {d.storage_kind === "drive" ? <ExternalLink size={14} /> : <Download size={14} />}
              </button>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => deleteDoc(d)}
                  data-testid={`client-doc-delete-${d.doc_id}`}
                  className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded"
                  title="Supprimer"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="client-doc-dialog">
          <DialogHeader>
            <DialogTitle>Upload administrative document</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Type de document</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="client-doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CONTRAT_CADRE" data-testid="client-doc-type-contrat">Contrat cadre</SelectItem>
                  <SelectItem value="AUTRE" data-testid="client-doc-type-autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {docType === "AUTRE" && (
              <div>
                <Label className="text-xs">Libellé / intitulé du document</Label>
                <Input
                  data-testid="client-doc-label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="NDA, Statuts, Avenant, RIB…"
                />
                <p className="text-[10px] text-zinc-500 mt-1">Même libellé = nouvelle version (v2, v3, …).</p>
              </div>
            )}

            <div>
              <Label className="text-xs">Fichier (PDF, DOCX, XLSX, images — max 25 MB)</Label>
              <Input
                ref={fileInputRef}
                type="file"
                data-testid="client-doc-file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {file && <p className="text-[11px] font-mono text-zinc-500 mt-1">{file.name} · {fmtSize(file.size)}</p>}
              <p className="text-[10px] text-zinc-500 mt-2">
                Le fichier sera automatiquement déposé dans le dossier Drive de ce client (si configuré). Sinon il reste stocké localement.
              </p>
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => setDialogOpen(false)}
              disabled={busy}
              className="px-4 py-2 text-sm rounded-md border border-zinc-200 bg-white hover:bg-zinc-50"
            >Annuler</button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid="client-doc-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-brand-navy text-white hover:bg-brand-navy/90 disabled:opacity-50"
            >
              {busy && <Loader2 size={13} className="animate-spin" />}
              <Upload size={13} /> Uploader
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
